import React, { createContext, PropsWithChildren, useContext } from 'react';
import { useImmer } from 'use-immer';
import { TemplateEditHandlerView } from './types';
import {
  DistributionTemplateInDTO,
  DistributionTypeEnum,
  EditDataLocationAndOrderOutDTO,
  EditTemplateCarrierRequirementInDTO,
  FindCarrierCarrierOrGroupDTO,
} from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import { createDistributionTemplateBody, templateLocationBody, toggleCreateDistribution } from '@app/pages/customer/TemplateEdit/templateEditMapping.ts';

interface TemplateEditHandlerContextType {
  state: TemplateEditHandlerView;
  updateRequirements: (expeditionTemplateId: string, data: EditTemplateCarrierRequirementInDTO, callback?: () => void) => void;
  updateTemplateName: (templateId: string, templateName: string, callback?: () => void) => void;
  updateCoverageRequest: (distributionTemplateId: string, data: DistributionTemplateInDTO, callback?: () => void) => void;
  updateTemplateLocation: (expeditionTemplateId: string, locationId: string, data: EditDataLocationAndOrderOutDTO, callback?: () => void) => void;
  changeDistributionType: (distributionType: DistributionTypeEnum) => void;
  changeDistributionPrice: (price: number) => void;
  changeDistributionSelectedItems: (items: FindCarrierCarrierOrGroupDTO[]) => void;
  toggleCreateDistribution: (defaultCurrency: string) => void;
  changeDistributionIntervalHour: (value: string) => void;
  createDistributionTemplate: () => void;
  changeCurrency: (currency: string) => void;
  changeAutomaticPublishing: (automaticallyPublish: boolean) => void;
  refreshDataFromDB: (onSuccess: () => void) => void;
}

export const TemplateEditHandlerContext = createContext<TemplateEditHandlerContextType>(undefined);

interface ComponentProps {
  initialData: TemplateEditHandlerView;
}

const TemplateEditHandlerProvider: React.FC<PropsWithChildren<ComponentProps>> = ({ initialData, children }) => {
  const [state, setState] = useImmer<TemplateEditHandlerView>(initialData);
  const {
    cuUpdateEditTemplateCarrierRequirement,
    cuGetTemplateData,
    cuUpdateEditTemplateName,
    cuUpdateEditDistribution,
    cuUpdateEditTemplateLocationAndOrder,
    cuCreateDistributionTemplate,
  } = useContext(ApiContext);

  const methods: TemplateEditHandlerContextType = {
    state,
    updateRequirements: (expeditionTemplateId: string, data: EditTemplateCarrierRequirementInDTO, callback = () => {}) => {
      cuUpdateEditTemplateCarrierRequirement(expeditionTemplateId, data, () => {
        cuGetTemplateData(expeditionTemplateId, result => {
          setState(draft => {
            return result;
          });
          callback();
        });
      });
    },
    updateTemplateName: (templateId: string, templateName: string, callback = () => {}) => {
      cuUpdateEditTemplateName(templateId, { templateName }, () => {
        cuGetTemplateData(templateId, result => {
          setState(draft => {
            return result;
          });
          callback();
        });
      });
    },
    updateCoverageRequest: (distributionTemplateId: string, data: DistributionTemplateInDTO, callback = () => {}) => {
      cuUpdateEditDistribution(distributionTemplateId, data, () => {
        cuGetTemplateData(distributionTemplateId, result => {
          setState(draft => {
            return result;
          });
          callback();
        });
      });
    },
    updateTemplateLocation: (expeditionTemplateId: string, locationId: string, data: EditDataLocationAndOrderOutDTO, callback = () => {}) => {
      cuUpdateEditTemplateLocationAndOrder(
        expeditionTemplateId,
        locationId,
        templateLocationBody(data),
        () => {
          cuGetTemplateData(expeditionTemplateId, result => {
            setState(draft => {
              return result;
            });
            callback();
          });
        },
      );
    },
    toggleCreateDistribution: (defaultCurrency: string) => {
      setState(draft => {
        toggleCreateDistribution(draft, defaultCurrency);
      });
    },
    changeDistributionType: (distributionType: DistributionTypeEnum) => {
      setState(draft => {
        draft.distribution.distributionType = distributionType;
      });
    },

    changeDistributionPrice: (price: number) => {
      setState(draft => {
        draft.distribution.price = price;
      });
    },
    changeDistributionSelectedItems: (items: FindCarrierCarrierOrGroupDTO[]) => {
      setState(draft => {
        draft.distribution.selectedItems = items;
      });
    },
    changeDistributionIntervalHour: (value: string) => {
      setState(draft => {
        const number = parseInt(value);
        draft.distribution.intervalHour = isNaN(number) ? null : number;
      });
    },
    createDistributionTemplate: () => {
      cuCreateDistributionTemplate(
        createDistributionTemplateBody(state),
        () => {
          cuGetTemplateData(state.templateExpeditionId, result => {
            setState(draft => {
              return result;
            });
          });
        },
      );
    },
    changeCurrency: (currency: string) => {
      setState(draft => {
        draft.distribution.currency = currency;
      });
    },
    changeAutomaticPublishing: (automaticallyPublish: boolean) => {
      setState(draft => {
        draft.distribution.isPublished = automaticallyPublish;
      });
    },
    refreshDataFromDB: (onSuccess: () => void) => {
      cuGetTemplateData(state.templateExpeditionId, result => {
        setState(draft => {
          onSuccess();
          return result;
        });
      });
    },
  };

  return <TemplateEditHandlerContext.Provider value={methods}>{children}</TemplateEditHandlerContext.Provider>;
};

export default TemplateEditHandlerProvider;
